import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { qty as fmtQty } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { SortHeader, useSort } from "@/components/SortHeader";
import { Plus, Trash2, ClipboardList, Search, FileText, Layers, X, PackageCheck, AlertTriangle, FileSpreadsheet, Tags, Store, Pencil, Check, Warehouse } from "lucide-react";
import { toast } from "sonner";

const STATUS_OPTS = [
  ["hazirlaniyor", "Hazırlanıyor", "bg-amber-500/15 text-amber-400"],
  ["sevk_edildi", "Sevk Edildi", "bg-sky-500/15 text-sky-300"],
  ["teslim_edildi", "Teslim Edildi", "bg-emerald-500/15 text-emerald-400"],
];
const statusInfo = (s) => STATUS_OPTS.find((x) => x[0] === s) || STATUS_OPTS[0];

const PALLET_GROUPS = {
  "201": { unit: "koli", per_layer: 15, max_layers: 8 },
  "202": { unit: "koli", per_layer: 15, max_layers: 8 },
  "214": { unit: "koli", per_layer: 15, max_layers: 8 },
  "421": { unit: "koli", per_layer: 15, max_layers: 8 },
  "7002": { unit: "kova", per_layer: 24, max_layers: 10 },
  "7003": { unit: "kova", per_layer: 24, max_layers: 10 },
  "7302": { unit: "kova", per_layer: 24, max_layers: 10 },
  "8302": { unit: "kova", per_layer: 24, max_layers: 10 },
  "263": { unit: "adet", per_layer: 10, max_layers: 11 },
};

function packPallets(lines) {
  const pallets = [];
  const ungrouped = [];
  let cur = null;
  for (const it of lines) {
    const g = PALLET_GROUPS[(it.code || "").trim()];
    const q = parseFloat(it.quantity) || 0;
    if (q <= 0) continue;
    if (!g) {
      ungrouped.push({ ...it, quantity: q });
      continue;
    }
    const capFull = g.per_layer * g.max_layers;
    let rem = q;
    while (rem > 1e-9) {
      if (!cur || cur.fill >= 0.999999) {
        cur = { items: [], fill: 0 };
        pallets.push(cur);
      }
      let cap = (1 - cur.fill) * capFull;
      if (cap <= 1e-9) {
        cur = { items: [], fill: 0 };
        pallets.push(cur);
        cap = capFull;
      }
      const take = Math.min(rem, cap);
      cur.items.push({ name: it.name, code: it.code, unit: g.unit, quantity: take, layers: take / g.per_layer });
      cur.fill += take / capFull;
      rem -= take;
    }
  }
  pallets.forEach((p, i) => {
    p.no = i + 1;
    p.fill_pct = Math.round(p.fill * 1000) / 10;
  });
  return { pallets, ungrouped };
}

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const [customerId, setCustomerId] = useState("");
  const [branch, setBranch] = useState("");
  const [branches, setBranches] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const pendingBranch = useRef(null);
  const [branchOpen, setBranchOpen] = useState(false);
  const [bForm, setBForm] = useState({ name: "", address: "", phone: "", contact_person: "" });
  const [manageOpen, setManageOpen] = useState(false);
  const [editBranch, setEditBranch] = useState(null);
  const [ebForm, setEbForm] = useState({ name: "", address: "", phone: "", contact_person: "" });
  const [histBranch, setHistBranch] = useState(null);
  const [hist, setHist] = useState([]);
  const [orderDate, setOrderDate] = useState(new Date().toISOString().slice(0, 10));
  const [deliveryDate, setDeliveryDate] = useState("");
  const [note, setNote] = useState("");
  const [ndp, setNdp] = useState("");
  const [lines, setLines] = useState([]);

  const [prodSearch, setProdSearch] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const [fSearch, setFSearch] = useState("");
  const [fStatus, setFStatus] = useState("all");
  const [fCustomer, setFCustomer] = useState("all");

  const loadOrders = async () => setOrders(await api.orders());
  const loadCustomers = async () => {
    const cs = await api.contacts();
    setCustomers(cs.filter((c) => c.type === "customer"));
  };
  useEffect(() => {
    loadOrders();
    loadCustomers();
  }, []);

  useEffect(() => {
    if (!prodSearch.trim()) {
      setResults([]);
      return;
    }
    let active = true;
    setSearching(true);
    api.stock({ category: "mamul", search: prodSearch.trim(), limit: 15 }).then((d) => {
      if (active) setResults(d.items || []);
    }).finally(() => active && setSearching(false));
    return () => { active = false; };
  }, [prodSearch]);

  useEffect(() => {
    if (!customerId) { setBranches([]); setBranch(""); return; }
    setBranch(pendingBranch.current ?? "");
    pendingBranch.current = null;
    api.contactBranches(customerId).then(setBranches).catch(() => setBranches([]));
  }, [customerId]);

  const saveBranch = async () => {
    if (!bForm.name.trim()) { toast.error("Şube/mağaza adı zorunlu"); return; }
    try {
      const nb = await api.addBranch(customerId, bForm);
      setBranches((prev) => [...prev, nb].sort((a, b) => (a.name || "").localeCompare(b.name || "")));
      setBranch(nb.name);
      setBranchOpen(false);
      setBForm({ name: "", address: "", phone: "", contact_person: "" });
      toast.success("Şube/mağaza kaydedildi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    }
  };

  const delBranchRow = async (b) => {
    await api.deleteBranch(b.id);
    setBranches((prev) => prev.filter((x) => x.id !== b.id));
    if (branch === b.name) setBranch("");
    if (histBranch?.id === b.id) { setHistBranch(null); setHist([]); }
    toast.success("Şube silindi");
  };

  const startEditBranch = (b) => {
    setEditBranch(b);
    setEbForm({ name: b.name, address: b.address || "", phone: b.phone || "", contact_person: b.contact_person || "" });
  };

  const saveEditBranch = async () => {
    if (!ebForm.name.trim()) { toast.error("Şube adı zorunlu"); return; }
    try {
      const nb = await api.updateBranch(editBranch.id, ebForm);
      setBranches((prev) => prev.map((x) => (x.id === nb.id ? nb : x)).sort((a, b) => (a.name || "").localeCompare(b.name || "")));
      setEditBranch(null);
      toast.success("Şube güncellendi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Güncellenemedi");
    }
  };

  const viewHistory = async (b) => {
    if (histBranch?.id === b.id) { setHistBranch(null); setHist([]); return; }
    setHistBranch(b);
    const d = await api.orders({ customer_id: customerId, branch: b.name });
    setHist(d);
  };

  const addLine = (s) => {
    if (lines.some((l) => l.stock_id === s.id)) {      toast.info("Bu ürün zaten eklendi");
      return;
    }
    setLines((prev) => [...prev, { stock_id: s.id, name: s.name, code: s.code || "", unit: s.unit || "", available: s.quantity, quantity: "1" }]);
    setProdSearch("");
    setResults([]);
  };

  const updateLine = (id, q) => setLines((prev) => prev.map((l) => (l.stock_id === id ? { ...l, quantity: q } : l)));
  const removeLine = (id) => setLines((prev) => prev.filter((l) => l.stock_id !== id));

  const { pallets, ungrouped } = useMemo(() => packPallets(lines), [lines]);

  const filteredOrders = useMemo(() => {
    const q = fSearch.trim().toLowerCase();
    return orders.filter((o) => {
      if (fStatus !== "all" && statusInfo(o.status)[0] !== fStatus) return false;
      if (fCustomer !== "all" && o.customer_id !== fCustomer) return false;
      if (q && !`${o.order_no} ${o.customer_name || ""} ${o.branch || ""}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [orders, fSearch, fStatus, fCustomer]);

  const orderSort = useSort(
    filteredOrders,
    {
      order_no: (o) => o.order_no,
      customer_name: (o) => o.customer_name || "",
      branch: (o) => o.branch || "",
      teslim: (o) => o.delivery_date || o.order_date || "",
      items: (o) => o.items?.length || 0,
      pallet_count: (o) => o.pallet_count || 0,
      status: (o) => statusInfo(o.status)[1],
    }
  );
  const sortedOrders = orderSort.sorted;

  const resetForm = () => {
    setCustomerId(""); setBranch(""); setBranches([]); setDeliveryDate(""); setNote(""); setNdp(""); setLines([]); setEditingId(null);
    setOrderDate(new Date().toISOString().slice(0, 10));
  };

  const editOrder = (o) => {
    setEditingId(o.id);
    pendingBranch.current = o.branch || "";
    setCustomerId(o.customer_id || "");
    setBranch(o.branch || "");
    setOrderDate((o.order_date || "").slice(0, 10) || new Date().toISOString().slice(0, 10));
    setDeliveryDate((o.delivery_date || "").slice(0, 10));
    setNote(o.note || "");
    setNdp(o.ndp || "");
    setLines((o.items || []).map((it) => ({ stock_id: it.stock_id, name: it.name, code: it.code || "", unit: it.unit || "", available: it.stock_before ?? 0, quantity: String(it.quantity) })));
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async () => {
    if (lines.length === 0) {
      toast.error("En az bir ürün ekleyin");
      return;
    }
    const cust = customers.find((c) => c.id === customerId);
    setSaving(true);
    try {
      const payload = {
        customer_id: customerId || null,
        customer_name: cust?.name || "",
        branch,
        order_date: orderDate,
        delivery_date: deliveryDate,
        note,
        ndp,
        items: lines.map((l) => ({ stock_id: l.stock_id, quantity: parseFloat(l.quantity) || 0 })),
      };
      const o = editingId ? await api.updateOrder(editingId, payload) : await api.createOrder(payload);
      toast.success(editingId ? `Sipariş güncellendi: ${o.order_no} · ${o.pallet_count} palet` : `Sipariş oluşturuldu: ${o.order_no} · ${o.pallet_count} palet`);
      setShowForm(false);
      resetForm();
      loadOrders();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sipariş kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async (o) => {
    try {
      await api.downloadExport(`/export/order/${o.id}`, `${o.order_no}.pdf`);
      toast.success("Sipariş fişi indirildi");
    } catch {
      toast.error("PDF oluşturulamadı");
    }
  };

  const changeStatus = async (o, status) => {
    try {
      await api.updateOrderStatus(o.id, status);
      setOrders((prev) => prev.map((x) => (x.id === o.id ? { ...x, status } : x)));
      toast.success(`Durum: ${statusInfo(status)[1]}`);
    } catch {
      toast.error("Durum güncellenemedi");
    }
  };

  const exportOrder = async (o, fmt) => {
    try {
      if (fmt === "xlsx") await api.downloadExport(`/export/order/${o.id}?format=xlsx`, `${o.order_no}.xlsx`);
      else await api.downloadExport(`/export/order/${o.id}/labels`, `${o.order_no}_etiketler.pdf`);
      toast.success(fmt === "xlsx" ? "Excel indirildi" : "Palet etiketleri indirildi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İndirilemedi");
    }
  };

  const downloadDepot = async (o) => {
    try {
      await api.downloadExport(`/export/order/${o.id}/depot`, `Fisier comanda TCE_BISC_${o.order_no}.xlsx`);
      toast.success("Depo Excel dosyası indirildi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İndirilemedi");
    }
  };

  const exportAll = async () => {
    try {
      await api.downloadExport(`/export/orders`, `arslan_siparisler.xlsx`);
      toast.success("Sipariş listesi Excel indirildi");
    } catch {
      toast.error("İndirilemedi");
    }
  };

  const remove = async (o) => {
    await api.deleteOrder(o.id);
    setOrders((prev) => prev.filter((x) => x.id !== o.id));
    toast.success("Sipariş silindi, stok geri yüklendi");
  };

  return (
    <div className="p-5 pt-20 lg:p-10 lg:pt-10">
      <PageHeader
        title="Sipariş Gir"
        subtitle="Müşteri siparişi oluştur · karışık palet doğrulaması · onayda mamül stoğu düşer"
        action={
          <div className="flex items-center gap-2">
            {orders.length > 0 && (
              <Button data-testid="orders-export-button" variant="outline" onClick={exportAll} className="gap-1.5"><FileSpreadsheet className="h-4 w-4 text-emerald-400" /> Excel</Button>
            )}
            <Button data-testid="new-order-button" onClick={() => { setShowForm((v) => !v); if (showForm) resetForm(); }} className="bg-primary text-primary-foreground hover:bg-primary/90">
              {showForm ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
              <span className="ml-1.5">{showForm ? "Vazgeç" : "Yeni Sipariş"}</span>
            </Button>
          </div>
        }
      />

      {showForm && (
        <div className="mb-3 flex items-center gap-2 text-sm font-medium" data-testid="order-form-mode">
          {editingId ? <><Pencil className="h-4 w-4 text-sky-400" /> Sipariş düzenleniyor</> : <><Plus className="h-4 w-4 text-primary" /> Yeni sipariş</>}
        </div>
      )}
      {showForm && (
        <div data-testid="order-form" className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Left: order details + products */}
          <div className="space-y-5 rounded-xl border border-border bg-card p-5">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label>Müşteri</Label>
                <Select value={customerId} onValueChange={setCustomerId}>
                  <SelectTrigger data-testid="order-customer-select" className="mt-1.5"><SelectValue placeholder="Müşteri seçin" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label>Şube / Mağaza</Label>
                  {customerId && branches.length > 0 && (
                    <button type="button" data-testid="manage-branches-btn" onClick={() => setManageOpen(true)} className="text-xs text-primary hover:underline">Şubeleri Yönet</button>
                  )}
                </div>
                <Select
                  value={branch || undefined}
                  disabled={!customerId}
                  onValueChange={(v) => { if (v === "__add__") { setBranchOpen(true); } else { setBranch(v); } }}
                >
                  <SelectTrigger data-testid="order-branch-select" className="mt-1.5"><SelectValue placeholder={customerId ? "Şube/mağaza seçin" : "Önce müşteri seçin"} /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {branches.map((b) => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}
                    <SelectItem value="__add__" data-testid="order-branch-add-option">+ Şube/Mağaza Ekle</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Sipariş Tarihi</Label><Input data-testid="order-date-input" type="date" value={orderDate} onChange={(e) => setOrderDate(e.target.value)} className="mt-1.5" /></div>
              <div><Label>Teslim Tarihi</Label><Input data-testid="order-delivery-input" type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} className="mt-1.5" /></div>
            </div>
            <div><Label>Not</Label><Input data-testid="order-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="İsteğe bağlı" className="mt-1.5" /></div>
            <div><Label>NDP / Aviz Kodu</Label><Input data-testid="order-ndp-input" value={ndp} onChange={(e) => setNdp(e.target.value)} placeholder="Örn: AVIZ BIFAAVZ 0528" className="mt-1.5" /></div>

            <div className="relative">
              <Label>Ürün Ekle (Mamül)</Label>
              <div className="relative mt-1.5">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input data-testid="order-product-search" value={prodSearch} onChange={(e) => setProdSearch(e.target.value)} placeholder="Ürün adı / kod ile ara..." className="pl-9" />
              </div>
              {(results.length > 0 || searching) && (
                <div className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-border bg-card shadow-xl">
                  {searching && <div className="px-3 py-2 text-xs text-muted-foreground">Aranıyor...</div>}
                  {results.map((s) => (
                    <button key={s.id} data-testid="order-product-result" onClick={() => addLine(s)} className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-secondary">
                      <span className="truncate">{s.code ? <span className="mr-1.5 font-mono text-teal-300">{s.code}</span> : null}{s.name}</span>
                      <span className="shrink-0 font-mono text-xs text-muted-foreground">{fmtQty(s.quantity)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              {lines.length === 0 && <div className="rounded-lg border border-dashed border-border py-6 text-center text-sm text-muted-foreground">Henüz ürün eklenmedi</div>}
              {lines.map((l) => {
                const g = PALLET_GROUPS[(l.code || "").trim()];
                const insufficient = (parseFloat(l.quantity) || 0) > (l.available ?? 0);
                return (
                  <div key={l.stock_id} data-testid="order-line" className={`flex items-center gap-3 rounded-lg border bg-secondary/30 p-2.5 ${insufficient ? "border-rose-500/60" : "border-border"}`}>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">{l.code ? <span className="mr-1.5 font-mono text-xs text-teal-300">{l.code}</span> : null}{l.name}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {g ? `Palet: ${g.per_layer} ${g.unit}/kat · max ${g.max_layers} kat` : <span className="text-amber-400">Palet kuralı tanımsız</span>}
                        {` · stok: ${fmtQty(l.available)}`}
                      </div>
                      {insufficient && <div data-testid="order-line-warning" className="mt-1 flex items-center gap-1 text-[11px] font-semibold text-rose-400"><AlertTriangle className="h-3 w-3" /> Stok yetersiz (mevcut: {fmtQty(l.available)})</div>}
                    </div>
                    <Input data-testid="order-line-qty" type="number" value={l.quantity} onChange={(e) => updateLine(l.stock_id, e.target.value)} className={`h-9 w-24 text-right font-mono ${insufficient ? "border-rose-500/60 text-rose-300" : ""}`} />
                    <span className="w-10 text-xs text-muted-foreground">{g?.unit || l.unit || ""}</span>
                    <button data-testid="order-line-remove" onClick={() => removeLine(l.stock_id)} className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-rose-500/15 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
                  </div>
                );
              })}
            </div>

            <Button data-testid="order-save-button" onClick={save} disabled={saving || lines.length === 0} className="w-full bg-primary text-primary-foreground hover:bg-primary/90">
              <PackageCheck className="h-4 w-4" /> <span className="ml-1.5">{saving ? "Kaydediliyor..." : editingId ? "Değişiklikleri Kaydet" : "Siparişi Kaydet"}</span>
            </Button>
          </div>

          {/* Right: live pallet preview */}
          <div className="space-y-4 rounded-xl border border-border bg-card p-5">
            <div className="flex items-center gap-2 text-sm font-semibold"><Layers className="h-4 w-4 text-primary" /> Palet Önizleme — {pallets.length} palet</div>
            {pallets.length === 0 && ungrouped.length === 0 && <div className="py-8 text-center text-sm text-muted-foreground">Ürün ekleyince palet dağılımı burada görünür</div>}
            {pallets.map((p) => (
              <div key={p.no} data-testid="pallet-card" className="rounded-lg border border-border p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-semibold">Palet {p.no}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${p.fill_pct > 100.01 ? "bg-rose-500/15 text-rose-400" : "bg-emerald-500/15 text-emerald-400"}`}>Doluluk %{p.fill_pct}</span>
                </div>
                <div className="mb-2 h-1.5 w-full overflow-hidden rounded-full bg-secondary">
                  <div className={`h-full ${p.fill_pct > 100.01 ? "bg-rose-400" : "bg-primary"}`} style={{ width: `${Math.min(100, p.fill_pct)}%` }} />
                </div>
                <div className="space-y-1">
                  {p.items.map((i, idx) => (
                    <div key={idx} className="flex items-center justify-between text-xs">
                      <span className="truncate text-muted-foreground">{i.code ? <span className="mr-1 font-mono text-teal-300">{i.code}</span> : null}{i.name}</span>
                      <span className="shrink-0 font-mono">{fmtQty(i.quantity)} {i.unit} · {Math.round(i.layers * 100) / 100} kat</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {ungrouped.length > 0 && (
              <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3">
                <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-amber-400"><AlertTriangle className="h-3.5 w-3.5" /> Palet kuralı tanımsız</div>
                {ungrouped.map((u, i) => (
                  <div key={i} className="text-xs text-muted-foreground">{u.code ? `${u.code} · ` : ""}{u.name} — {fmtQty(u.quantity)}</div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="orders-filter-search" value={fSearch} onChange={(e) => setFSearch(e.target.value)} placeholder="Sipariş no / müşteri / şube ara..." className="pl-9" />
        </div>
        <Select value={fStatus} onValueChange={setFStatus}>
          <SelectTrigger data-testid="orders-filter-status" className="w-full sm:w-44"><SelectValue placeholder="Durum" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Durumlar</SelectItem>
            {STATUS_OPTS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fCustomer} onValueChange={setFCustomer}>
          <SelectTrigger data-testid="orders-filter-customer" className="w-full sm:w-56"><SelectValue placeholder="Müşteri" /></SelectTrigger>
          <SelectContent className="max-h-64">
            <SelectItem value="all">Tüm Müşteriler</SelectItem>
            {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {(fSearch || fStatus !== "all" || fCustomer !== "all") && (
          <Button data-testid="orders-filter-clear" variant="ghost" size="sm" onClick={() => { setFSearch(""); setFStatus("all"); setFCustomer("all"); }} className="gap-1.5 text-xs"><X className="h-3.5 w-3.5" /> Temizle</Button>
        )}
        <span className="text-xs text-muted-foreground sm:ml-auto">{filteredOrders.length} / {orders.length} sipariş</span>
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <SortHeader label="Sipariş No" sortId="order_no" sort={orderSort} />
              <SortHeader label="Müşteri" sortId="customer_name" sort={orderSort} />
              <SortHeader label="Şube" sortId="branch" sort={orderSort} />
              <SortHeader label="Teslim" sortId="teslim" sort={orderSort} />
              <SortHeader label="Ürün" sortId="items" sort={orderSort} align="right" />
              <SortHeader label="Palet" sortId="pallet_count" sort={orderSort} align="right" />
              <SortHeader label="Durum" sortId="status" sort={orderSort} align="center" />
              <th className="px-5 py-3.5 text-right font-semibold">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.length === 0 && <tr><td colSpan={8} className="px-5 py-12 text-center text-muted-foreground">{orders.length === 0 ? "Henüz sipariş yok" : "Filtreye uyan sipariş yok"}</td></tr>}
            {sortedOrders.map((o) => (
              <tr key={o.id} data-testid="order-row" className="border-b border-border/50 hover:bg-secondary/30">
                <td className="px-5 py-3 font-mono font-semibold text-primary">{o.order_no}</td>
                <td className="px-5 py-3">{o.customer_name || "-"}</td>
                <td className="px-5 py-3 text-muted-foreground">{o.branch || "-"}</td>
                <td className="px-5 py-3 text-muted-foreground">{o.delivery_date || o.order_date}</td>
                <td className="px-5 py-3 text-right font-mono">{o.items?.length || 0}</td>
                <td className="px-5 py-3 text-right font-mono">{o.pallet_count}</td>
                <td className="px-5 py-3 text-center">
                  <Select value={statusInfo(o.status)[0]} onValueChange={(v) => changeStatus(o, v)}>
                    <SelectTrigger data-testid="order-status-select" className={`mx-auto h-8 w-36 border-0 text-xs font-semibold ${statusInfo(o.status)[2]}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-5 py-3">
                  <div className="flex items-center justify-end gap-2">
                    <button data-testid="order-pdf-button" onClick={() => downloadPdf(o)} title="Sipariş Fişi (PDF)" className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-teal-500/15 hover:text-teal-400"><FileText className="h-4 w-4" /></button>
                    <button data-testid="order-excel-button" onClick={() => exportOrder(o, "xlsx")} title="Excel (palet dağılımı)" className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-emerald-500/15 hover:text-emerald-400"><FileSpreadsheet className="h-4 w-4" /></button>
                    <button data-testid="order-depot-button" onClick={() => downloadDepot(o)} title="Depo Excel (sevkiyat dosyası)" className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-amber-500/15 hover:text-amber-400"><Warehouse className="h-4 w-4" /></button>
                    <button data-testid="order-labels-button" onClick={() => exportOrder(o, "labels")} title="Palet Etiketleri (barkodlu PDF)" className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-indigo-500/15 hover:text-indigo-300"><Tags className="h-4 w-4" /></button>
                    <button data-testid="order-edit-button" onClick={() => editOrder(o)} title="Düzenle" className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-sky-500/15 hover:text-sky-400"><Pencil className="h-4 w-4" /></button>
                    <button data-testid="order-delete-button" onClick={() => remove(o)} title="Sil" className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-rose-500/15 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={branchOpen} onOpenChange={setBranchOpen}>
        <DialogContent className="bg-card">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display"><Store className="h-5 w-5 text-primary" /> Şube / Mağaza Ekle</DialogTitle>
            <DialogDescription>Bu müşteriye ait şube/mağaza bilgilerini girin. Kaydettikten sonra listeden seçebilirsiniz.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3.5 py-2">
            <div><Label>Şube / Mağaza Adı</Label><Input data-testid="branch-name-input" value={bForm.name} onChange={(e) => setBForm({ ...bForm, name: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Adres</Label><Input data-testid="branch-address-input" value={bForm.address} onChange={(e) => setBForm({ ...bForm, address: e.target.value })} className="mt-1.5" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Telefon</Label><Input data-testid="branch-phone-input" value={bForm.phone} onChange={(e) => setBForm({ ...bForm, phone: e.target.value })} className="mt-1.5" /></div>
              <div><Label>İlgili Kişi</Label><Input data-testid="branch-person-input" value={bForm.contact_person} onChange={(e) => setBForm({ ...bForm, contact_person: e.target.value })} className="mt-1.5" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="branch-save-button" onClick={saveBranch} className="bg-primary text-primary-foreground hover:bg-primary/90">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={manageOpen} onOpenChange={(v) => { setManageOpen(v); if (!v) { setEditBranch(null); setHistBranch(null); setHist([]); } }}>
        <DialogContent className="max-h-[85vh] overflow-y-auto bg-card">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display"><Store className="h-5 w-5 text-primary" /> Şubeleri Yönet</DialogTitle>
            <DialogDescription>Şubeleri düzenleyin/silin ve şube bazlı sipariş geçmişini görün.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            {branches.length === 0 && <div className="py-6 text-center text-sm text-muted-foreground">Kayıtlı şube yok</div>}
            {branches.map((b) => (
              <div key={b.id} data-testid="manage-branch-row" className="rounded-lg border border-border p-3">
                {editBranch?.id === b.id ? (
                  <div className="space-y-2">
                    <Input data-testid="eb-name" value={ebForm.name} onChange={(e) => setEbForm({ ...ebForm, name: e.target.value })} placeholder="Şube adı" className="h-8" />
                    <div className="grid grid-cols-2 gap-2">
                      <Input value={ebForm.address} onChange={(e) => setEbForm({ ...ebForm, address: e.target.value })} placeholder="Adres" className="h-8" />
                      <Input value={ebForm.phone} onChange={(e) => setEbForm({ ...ebForm, phone: e.target.value })} placeholder="Telefon" className="h-8" />
                    </div>
                    <Input value={ebForm.contact_person} onChange={(e) => setEbForm({ ...ebForm, contact_person: e.target.value })} placeholder="İlgili kişi" className="h-8" />
                    <div className="flex gap-2">
                      <Button data-testid="eb-save" size="sm" onClick={saveEditBranch} className="h-8 gap-1"><Check className="h-3.5 w-3.5" /> Kaydet</Button>
                      <Button size="sm" variant="outline" onClick={() => setEditBranch(null)} className="h-8">İptal</Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <div className="min-w-0">
                        <div className="truncate font-medium">{b.name}</div>
                        <div className="truncate text-xs text-muted-foreground">{[b.address, b.phone, b.contact_person].filter(Boolean).join(" · ") || "Detay yok"}</div>
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        <button data-testid="branch-history-btn" onClick={() => viewHistory(b)} title="Sipariş geçmişi" className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground hover:bg-teal-500/15 hover:text-teal-400"><ClipboardList className="h-3.5 w-3.5" /></button>
                        <button data-testid="branch-edit-btn" onClick={() => startEditBranch(b)} title="Düzenle" className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground hover:bg-primary/15 hover:text-primary"><Pencil className="h-3.5 w-3.5" /></button>
                        <button data-testid="branch-delete-btn" onClick={() => delBranchRow(b)} title="Sil" className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground hover:bg-rose-500/15 hover:text-rose-400"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </div>
                    {histBranch?.id === b.id && (
                      <div className="mt-2 border-t border-border pt-2">
                        <div className="mb-1 text-xs font-semibold text-muted-foreground">Sipariş Geçmişi ({hist.length})</div>
                        {hist.length === 0 ? <div className="text-xs text-muted-foreground">Bu şubeye ait sipariş yok</div> : hist.map((o) => (
                          <div key={o.id} className="flex items-center justify-between py-0.5 text-xs">
                            <span className="font-mono text-primary">{o.order_no}</span>
                            <span className="text-muted-foreground">{o.order_date} · {o.pallet_count} palet</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
